import React, { createContext, useContext, useState } from "react";
import {
  DrawerPosition,
  DEFAULT_UI_SETTINGS,
  parseUISettings,
  formatUISettings,
} from "../utils/userPreferencesHelper";
import userPreferenceApiService from "../services/user-preference-api";

interface DrawerContextType {
  drawerPosition: DrawerPosition;
  setDrawerPosition: (position: DrawerPosition) => void;
  initializeFromPreferences: (settingsField: string | undefined | null) => void;
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

const DRAWER_POSITION_KEY = "drawer-position";

export const DrawerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [drawerPosition, setDrawerPositionState] = useState<DrawerPosition>(
    () => {
      // Load drawer position from localStorage
      const savedPosition = localStorage.getItem(DRAWER_POSITION_KEY);
      return (
        (savedPosition as DrawerPosition) || DEFAULT_UI_SETTINGS.drawerPosition
      );
    },
  );

  const setDrawerPosition = async (position: DrawerPosition) => {
    setDrawerPositionState(position);
    localStorage.setItem(DRAWER_POSITION_KEY, position);

    // Save to backend
    try {
      // Get current preferences
      const currentPrefs = await userPreferenceApiService.getMyPreferences();

      // Parse current settings field to get display mode and dock position
      const uiSettings = parseUISettings(currentPrefs.settings);

      // Update drawer position
      uiSettings.drawerPosition = position;

      // Format and save
      const updatedSettingsField = formatUISettings(uiSettings);
      await userPreferenceApiService.saveMyPreferences({
        ...currentPrefs,
        settings: updatedSettingsField,
      });
    } catch (error) {
      console.error("Failed to save drawer position to backend:", error);
      // Continue with localStorage-only fallback
    }
  };

  const initializeFromPreferences = (
    settingsField: string | undefined | null,
  ) => {
    const uiSettings = parseUISettings(settingsField);
    setDrawerPositionState(uiSettings.drawerPosition);
    localStorage.setItem(DRAWER_POSITION_KEY, uiSettings.drawerPosition);
  };

  return (
    <DrawerContext.Provider
      value={{ drawerPosition, setDrawerPosition, initializeFromPreferences }}
    >
      {children}
    </DrawerContext.Provider>
  );
};

export const useDrawer = (): DrawerContextType => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error("useDrawer must be used within a DrawerProvider");
  }
  return context;
};
